import { Flex, Space, Typography } from "antd";
import { Toggle } from "@libertify/ui";
import { useTranslate } from "hooks/useTranslate/";
import { useTemplateEditorStore } from "../../storyboardEditor/store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../storyboardEditor/store/useTemplatesStatusStore";
import { useTemplateData } from "../../storyboardEditor/hooks/useTemplateData";
import { PanelSection } from "../../storyboardEditor/components/StoryboardMenuTabs/PanelSection";
import { ThemeApplyToAllButton } from "./ThemeApplyToAllButton";
import { ItemAlignmentFeature } from "../../storyboardEditor/components/features/ItemAlignmentFeature";
import { AvatarShapeFeature } from "../../storyboardEditor/components/StoryboardMenuTabs/AvatarTab/features/AvatarShapeFeature";
import { AvatarShapeColorFeature } from "../../storyboardEditor/components/StoryboardMenuTabs/AvatarTab/features/AvatarShapeColorFeature";
import { AvatarStrokeFeature } from "../../storyboardEditor/components/StoryboardMenuTabs/AvatarTab/features/AvatarStrokeFeature";
import { AvatarShape, Avatar } from "../../storyboardEditor/types";
import { PaletteResponse, PresenterProfileResponse } from "pages/media/types";
import { PresenterSelector } from "./PresenterSelector";
import { useCallback } from "react";
import {
  calculateNewItemSizeAndPosition,
  getRelativeDimentionsToCalculateRadius,
  loadImageRatio,
} from "../../storyboardEditor/helpers";
import { getAvatarImagePath } from "../../storyboardEditor/helpers/itemsHelper";
import { RadiusIcon } from "components/icons/";
import { BorderRadius } from "../../storyboardEditor/components/controls/BorderRadius";
import { useChangeLayer } from "../../storyboardEditor/hooks/useChangeLayer";
import { useThemeData } from "../hooks/useThemeData";

export const ThemePresenterTab = ({
  brandKitPalette,
  previewWidth,
  previewHeight,
}: {
  brandKitPalette: PaletteResponse;
  previewWidth: number;
  previewHeight: number;
}) => {
  const t = useTranslate();
  const { activePresenter } = useThemeData();

  const { presenter, setPresenter } = useTemplateEditorStore((store) => ({
    presenter: store.templateState.presenter,
    setPresenter: store.setPresenter,
  }));

  const {
    mediaGenerationSettingsData: { mediaAspectRatio },
  } = useTemplateData();

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const { newHighestLayerIndex } = useChangeLayer(presenter);

  const applyPresenterFromProfile = useCallback(
    async (presenterProfile: PresenterProfileResponse) => {
      const avatarImageUrl = getAvatarImagePath({
        ...presenter,
        presenter: presenterProfile,
      });

      if (!avatarImageUrl || !mediaAspectRatio) return;

      const ratio = await loadImageRatio(avatarImageUrl);
      const { size: newSize } = calculateNewItemSizeAndPosition(
        presenter.size.height === 0 ? 0.4 : presenter.size.height,
        presenter.position.x,
        ratio,
        mediaAspectRatio
      );

      setPresenter({
        presenter: presenterProfile,
        size: newSize,
        position: {
          x: newSize.width === 1 ? 0 : (1 - newSize.width) / 2,
          y: 1 - newSize.height,
        },
        layerIndex:
          presenter.layerIndex === 0
            ? newHighestLayerIndex
            : presenter.layerIndex,
      });
      addTemplateUpdate();
    },
    [
      presenter,
      setPresenter,
      addTemplateUpdate,
      mediaAspectRatio,
      newHighestLayerIndex,
    ]
  );

  const onAvatarVisibilityChange = (checked: boolean) => {
    if (!presenter.presenter && activePresenter) {
      if (checked) {
        applyPresenterFromProfile(activePresenter).catch((error) => {
          console.error("Error applying presenter from profile", error);
        });
      }
    } else {
      setPresenter({ presenter: checked ? activePresenter : undefined });
      addTemplateUpdate();
    }
  };

  if (!activePresenter?.face_profile)
    return (
      <PanelSection
        title={t("components.templates.settings.presenterTab.title")}
        subtitle={t("components.templates.settings.presenterTab.noFaceProfile")}
        primary
      />
    );

  const relativeDimensionsToCalculateRadius =
    getRelativeDimentionsToCalculateRadius(previewWidth, previewHeight);

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.presenterTab.title")}
        subtitle={t("components.templates.settings.presenterTab.description")}
        primary
      />
      <PanelSection
        title={t("components.templates.settings.presenterTab.myPresenters")}
      >
        <PresenterSelector />
        <Space align="center">
          <Toggle
            checked={!!presenter.presenter}
            onChange={onAvatarVisibilityChange}
          />
          <Typography.Text>
            {t("components.templates.settings.showItem", {
              item: t("components.templates.settings.presenter").toLowerCase(),
            })}
          </Typography.Text>
        </Space>
      </PanelSection>
      {presenter.presenter && (
        <>
          <PanelSection
            title={t("components.templates.settings.presenterTab.appearance")}
          >
            <AvatarShapeFeature
              brandKitPalette={brandKitPalette}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
            {presenter.avatarShape === AvatarShape.Box && (
              <Flex align="center" gap={8}>
                <RadiusIcon />
                <BorderRadius
                  value={presenter.stroke?.radius ?? 0}
                  maxValue={Math.round(
                    (presenter.size.width / 2) * previewWidth
                  )}
                  onChange={(value) => {
                    setPresenter({
                      stroke: { ...presenter.stroke, radius: value },
                    });
                    addTemplateUpdate();
                  }}
                  relativeDimentions={relativeDimensionsToCalculateRadius}
                  popover={false}
                  sliderProps={{
                    label: t(
                      "components.templates.settings.presenterTab.radius"
                    ),
                    style: {
                      marginLeft: 10,
                    },
                    containerStyle: undefined,
                  }}
                />
              </Flex>
            )}
          </PanelSection>
          {presenter.avatarShape !== AvatarShape.Regular && (
            <PanelSection
              title={t("components.templates.settings.circleColor")}
            >
              <AvatarShapeColorFeature brandKitPalette={brandKitPalette} />
            </PanelSection>
          )}
          {presenter.avatarShape !== AvatarShape.Regular && (
            <PanelSection title={t("components.templates.settings.stroke")}>
              <AvatarStrokeFeature
                brandKitPalette={brandKitPalette}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
              />
            </PanelSection>
          )}
          <PanelSection title={t("components.templates.settings.alignment")}>
            <ItemAlignmentFeature
              pickedItem={presenter}
              onChange={(value) => {
                setPresenter(value as Partial<Avatar>);
                addTemplateUpdate();
              }}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
          </PanelSection>
          <PanelSection>
            <ThemeApplyToAllButton
              partialTemplateState={{ presenter }}
              filter={(template) => !!template.presenter.presenter}
              elementName={t("media.themeEditor.elementAvatar")}
            />
          </PanelSection>
        </>
      )}
    </>
  );
};
